export type PartyContactValue = {
  first_name: string;
  last_name: string;
  contact_number: string;
  email_address: string;
  company_name: string;
  directory_id?: string;
};

export type MissingPartyField = { party: string; field: string; fieldPath: string };
export type PartyComplianceForm = {
  buyerContacts: PartyContactValue[];
  sellerContacts: PartyContactValue[];
  transferAttorney: PartyContactValue;
  bondAttorney: PartyContactValue;
  bondOriginator: PartyContactValue;
  financingType: string;
};
export type PartyValidationGate = { isSales: boolean; isCreating: boolean; previousStatus?: string | null; nextStatus: string | null };
export type ProfessionalPartyKey = 'transfer_attorney_contact' | 'bond_attorney_contact' | 'bond_originator_contact';

export function getSalesStatusEditPolicy(isSales: boolean, status: string | null | undefined, isRegionalAdmin: boolean) {
  const registeredLocked = isSales && status?.trim().toLowerCase() === 'registered';
  return { registeredLocked, canOpenEditor: !registeredLocked || isRegionalAdmin, canRevert: registeredLocked && isRegionalAdmin };
}

const requiredContactFields = [
  ['first_name', 'First Name'], ['last_name', 'Last Name'], ['contact_number', 'Contact Number'], ['email_address', 'Email Address'],
] as const;
const requiredProfessionalFields = [...requiredContactFields, ['company_name', 'Company Name'] as const];
function hasValue(value: string | null | undefined): boolean { return typeof value === 'string' && value.trim().length > 0; }

function addMissingFields(missing: MissingPartyField[], party: string, path: string, contact: PartyContactValue, fields: ReadonlyArray<readonly [keyof PartyContactValue, string]>): void {
  for (const [key, label] of fields) if (!hasValue(contact[key])) missing.push({ party, field: label, fieldPath: `${path}.${key}` });
}

export function shouldValidatePartyForm(gate: PartyValidationGate): boolean {
  if (!gate.isSales) return false;
  if (gate.isCreating) return true;
  const previous = (gate.previousStatus ?? '').trim().toLowerCase();
  const next = (gate.nextStatus ?? '').trim().toLowerCase();
  return previous !== 'registered' && previous !== next;
}

export function validatePartyForm(form: PartyComplianceForm): MissingPartyField[] {
  const missing: MissingPartyField[] = [];
  if (!form.buyerContacts.length) missing.push({ party: 'Buyer', field: 'At least one Buyer', fieldPath: 'buyer_contacts' });
  form.buyerContacts.forEach((contact, index) => addMissingFields(missing, `Buyer ${index + 1}`, `buyer_contacts[${index}]`, contact, requiredContactFields));
  if (!form.sellerContacts.length) missing.push({ party: 'Seller', field: 'At least one Seller', fieldPath: 'seller_contacts' });
  form.sellerContacts.forEach((contact, index) => addMissingFields(missing, `Seller ${index + 1}`, `seller_contacts[${index}]`, contact, requiredContactFields));
  addMissingFields(missing, 'Transferring Attorney', 'transfer_attorney', form.transferAttorney, requiredProfessionalFields);
  const financingType = form.financingType.trim().toLowerCase();
  const isCash = financingType === 'cash';
  const isBonded = financingType === 'bonded' || financingType === 'bond';
  if (!isCash && !isBonded) missing.push({ party: 'Financing', field: 'Cash or Bonded', fieldPath: 'transaction_financing_type' });
  if (isBonded) addMissingFields(missing, 'Bond Originator', 'bond_originator', form.bondOriginator, requiredProfessionalFields);
  const bondAttorneyEntered = Object.values(form.bondAttorney).some(hasValue);
  if (isBonded && bondAttorneyEntered) addMissingFields(missing, 'Bond Attorney', 'bond_attorney', form.bondAttorney, requiredProfessionalFields);
  return missing;
}

export function getPartyCorrectionTab(fieldPath: string): 'details' | 'parties' {
  return fieldPath === 'transaction_financing_type' ? 'details' : 'parties';
}

export function normalizeFinancingType(value: string | null | undefined): string {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'cash') return 'Cash';
  if (normalized === 'bond' || normalized === 'bonded') return 'Bonded';
  return value?.trim() ?? '';
}

export function getProfessionalPartyInputPolicy(key: ProfessionalPartyKey, isSales: boolean, financingValue: string) {
  const financeContact = key === 'bond_attorney_contact' || key === 'bond_originator_contact';
  const financingType = normalizeFinancingType(financingValue);
  return {
    disabled: isSales && financingType === 'Cash' && financeContact,
    required: isSales && (key === 'transfer_attorney_contact' || (key === 'bond_originator_contact' && financingType === 'Bonded')),
  };
}

export type TransactionPartiesSource = {
  party_contact_details?: Record<string, unknown> | null;
  buyer?: string | null;
  seller?: string | null;
  transfer_attorney?: string | null;
  ta_mobile_phone?: string | null;
  ta_email?: string | null;
  bond_attorney?: string | null;
  ba_mobile_phone?: string | null;
  ba_email?: string | null;
  bond_originator?: string | null;
  transaction_financing_type?: string | null;
};
export type PartyViewContact = { name: string; companyName: string; contactNumber: string; emailAddress: string };
function contactText(value: unknown): string { return typeof value === 'string' ? value.trim() : ''; }
function mapPartyViewContact(input: unknown, name?: string | null, phone?: string | null, email?: string | null): PartyViewContact {
  const contact = input && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {};
  return {
    name: [contactText(contact.first_name), contactText(contact.last_name)].filter(Boolean).join(' ') || contactText(name),
    companyName: contactText(contact.company_name),
    contactNumber: contactText(contact.contact_number) || contactText(contact.phone) || contactText(phone),
    emailAddress: contactText(contact.email_address) || contactText(contact.email) || contactText(email),
  };
}

export function buildTransactionPartiesView(source: TransactionPartiesSource) {
  const details = source.party_contact_details ?? {};
  const mapContacts = (key: 'buyer' | 'seller') => {
    const entries = details[`${key}_contacts`];
    const contacts = Array.isArray(entries) && entries.length > 0 ? entries : [details[key]];
    return contacts.map((contact, index) => mapPartyViewContact(contact, index === 0 ? source[key] : null));
  };
  const financingType = normalizeFinancingType(source.transaction_financing_type);
  const professionals = [
    { label: 'Transferring Attorney', contact: mapPartyViewContact(details.transfer_attorney, source.transfer_attorney, source.ta_mobile_phone, source.ta_email), bond: false },
    { label: 'Bond Attorney', contact: mapPartyViewContact(details.bond_attorney, source.bond_attorney, source.ba_mobile_phone, source.ba_email), bond: true },
    { label: 'Bond Originator', contact: mapPartyViewContact(details.bond_originator, source.bond_originator), bond: true },
  ].map(({ label, contact, bond }) => {
    const hasDetails = Object.values(contact).some(Boolean);
    const cashBondContact = financingType === 'Cash' && bond;
    return { label, contact, historical: cashBondContact && hasDetails, notApplicable: cashBondContact && !hasDetails };
  });
  return { financingType, buyers: mapContacts('buyer'), sellers: mapContacts('seller'), professionals };
}
